import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { api } from '../api.ts';
import BrandMark from '../components/BrandMark.tsx';
import { Icon } from '../components/Icons.tsx';
import RecoveryCodes from '../components/RecoveryCodes.tsx';
import { biometricName, canUseBiometrics, deviceHasPasskey, passkeyError, registerPasskey, signInWithPasskey } from '../session.ts';

type Mode =
  | 'login' | 'register'
  | 'forgot'        // choose Face ID or recovery code
  | 'newPassword'   // after a Face ID sign-in from "forgot"
  | 'codes'         // show recovery codes after sign-up
  | 'offerBio';     // offer to turn on Face ID after a password sign-in

function Card({ title, children, onSubmit }: { title?: ReactNode; children: ReactNode; onSubmit?: (e: FormEvent) => void }) {
  const body = (
    <>
      <div className="brand">{title ?? <><BrandMark size={34} /><span className="wordmark">Edgebook</span></>}</div>
      {children}
    </>
  );
  return (
    <div className="auth-wrap">
      {onSubmit
        ? <form className="card auth-card" onSubmit={onSubmit}>{body}</form>
        : <div className="card auth-card">{body}</div>}
    </div>
  );
}

export default function Login({ onSignedIn }: { onSignedIn: () => Promise<void> }) {
  const [status, setStatus] = useState<{ hasUsers: boolean; signupOpen: boolean; invite: 'valid' | 'invalid' | null } | null>(null);
  // An invite link looks like https://edgebook…/?invite=TOKEN
  const [invite] = useState(() => new URLSearchParams(window.location.search).get('invite') ?? '');
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[]>([]);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [bio, setBio] = useState(false);

  useEffect(() => {
    let timer = 0;
    const load = () => api.get<{ hasUsers: boolean; signupOpen: boolean; invite: 'valid' | 'invalid' | null }>(`/api/auth/status${invite ? `?invite=${encodeURIComponent(invite)}` : ''}`).then((s) => {
      setStatus(s);
      if (!s.hasUsers || s.invite === 'valid') setMode('register');
    }).catch(() => { timer = window.setTimeout(load, 3000); });
    void load();
    canUseBiometrics().then(setBio);
    return () => clearTimeout(timer);
  }, []);

  const go = (m: Mode) => { setMode(m); setError(''); setPassword(''); };
  const name = biometricName();

  /** Runs an async action with busy/error handling. Passkey cancellations are silent. */
  async function attempt(fn: () => Promise<void>) {
    setError('');
    setBusy(true);
    try { await fn(); }
    catch (err) {
      const msg = passkeyError(err);
      if (msg) setError(msg);
    } finally { setBusy(false); }
  }

  // After any password-based sign-in, offer Face ID on devices that have it.
  const finish = async () => (bio && !deviceHasPasskey() ? go('offerBio') : onSignedIn());

  const submitLogin = (e: FormEvent) => {
    e.preventDefault();
    void attempt(async () => {
      await api.post('/api/auth/login', { email, password });
      await finish();
    });
  };

  const submitRegister = (e: FormEvent) => {
    e.preventDefault();
    void attempt(async () => {
      const r = await api.post<{ recoveryCodes: string[] }>('/api/auth/register', { email, password, invite: invite || undefined });
      if (invite) window.history.replaceState(null, '', window.location.pathname); // the link is used up
      setCodes(r.recoveryCodes);
      go('codes');
    });
  };

  const submitRecovery = (e: FormEvent) => {
    e.preventDefault();
    void attempt(async () => {
      const r = await api.post<{ recoveryCodesLeft: number }>('/api/auth/recover', { email, code, password });
      setCode('');
      setNotice(r.recoveryCodesLeft <= 3
        ? `Password changed. Only ${r.recoveryCodesLeft} recovery code${r.recoveryCodesLeft === 1 ? '' : 's'} left; make new ones in Settings.`
        : `Password changed. ${r.recoveryCodesLeft} recovery codes left.`);
      await finish();
    });
  };

  const submitNewPassword = (e: FormEvent) => {
    e.preventDefault();
    void attempt(async () => {
      await api.post('/api/auth/password', { next: password });
      await onSignedIn();
    });
  };

  const faceIdSignIn = (then: () => Promise<void>) => attempt(async () => {
    await signInWithPasskey();
    await then();
  });

  if (!status) return null;

  if (mode === 'codes') {
    return (
      <Card title="Save your recovery codes">
        <RecoveryCodes codes={codes} email={email} onDone={() => void finish()} />
      </Card>
    );
  }

  if (mode === 'offerBio') {
    return (
      <Card title={<><Icon.face /> Use {name}?</>}>
        {notice && <div className="banner">{notice}</div>}
        <p className="muted" style={{ margin: 0, textAlign: 'center' }}>
          Sign in next time with {name} instead of typing your password on this device.
          It also lets you reset your password if you forget it.
        </p>
        {error && <div className="error">{error}</div>}
        <button className="primary" disabled={busy} onClick={() => attempt(async () => { await registerPasskey(); await onSignedIn(); })}>
          Turn on {name}
        </button>
        <button className="ghost" disabled={busy} onClick={() => onSignedIn()}>Not now</button>
      </Card>
    );
  }

  if (mode === 'newPassword') {
    return (
      <Card title="Choose a new password" onSubmit={submitNewPassword}>
        <p className="muted small" style={{ margin: 0, textAlign: 'center' }}>You're signed in with {name}. Other devices will be signed out.</p>
        <label className="field">New password
          <input type="password" autoComplete="new-password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus />
        </label>
        {error && <div className="error" role="alert">{error}</div>}
        <button className="primary" type="submit" disabled={busy}>Save password</button>
        <button type="button" className="ghost small" onClick={() => onSignedIn()}>Skip for now</button>
      </Card>
    );
  }

  if (mode === 'forgot') {
    return (
      <Card title="Reset your password" onSubmit={submitRecovery}>
        {bio && (
          <>
            <button type="button" className="primary" disabled={busy} onClick={() => faceIdSignIn(async () => go('newPassword'))}>
              <Icon.face /> Use {name}
            </button>
            <div className="divider">or use a recovery code</div>
          </>
        )}
        <label className="field">Email
          <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </label>
        <label className="field">Recovery code
          <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="xxxxx-xxxxx" autoComplete="one-time-code"
            autoCapitalize="none" spellCheck={false} required className="mono" />
        </label>
        <label className="field">New password
          <input type="password" autoComplete="new-password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} required />
        </label>
        {error && <div className="error" role="alert">{error}</div>}
        <button className={bio ? '' : 'primary'} type="submit" disabled={busy}>Reset password</button>
        <button type="button" className="ghost small" onClick={() => go('login')}>Back to sign in</button>
      </Card>
    );
  }

  const registering = mode === 'register';
  return (
    <Card onSubmit={registering ? submitRegister : submitLogin}>
      {registering && !status.hasUsers && (
        <p className="muted small" style={{ margin: 0, textAlign: 'center' }}>Create your account to get started.</p>
      )}
      {registering && status.invite === 'valid' && (
        <div className="banner" style={{ textAlign: 'center' }}>You've been invited to Edgebook. Create your account to get started.</div>
      )}
      {status.invite === 'invalid' && (
        <div className="banner warn">This invite link has expired or was already used. Ask the person who sent it for a new one.</div>
      )}

      {!registering && bio && deviceHasPasskey() && (
        <>
          <button type="button" className="primary" onClick={() => faceIdSignIn(onSignedIn)} disabled={busy}><Icon.face /> Sign in with {name}</button>
          <div className="divider">or use your password</div>
        </>
      )}

      <label className="field">Email
        <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus={!deviceHasPasskey()} />
      </label>
      <label className="field">
        <span className="row">Password<span className="spacer" />
          {!registering && <button type="button" className="link-btn small" onClick={() => go('forgot')}>Forgot password?</button>}
        </span>
        <input type="password" autoComplete={registering ? 'new-password' : 'current-password'} value={password}
          onChange={(e) => setPassword(e.target.value)} required minLength={registering ? 8 : undefined} />
      </label>
      {error && <div className="error" role="alert">{error}</div>}
      <button className={!registering && bio && deviceHasPasskey() ? '' : 'primary'} type="submit" disabled={busy}>
        {registering ? 'Create account' : 'Sign in'}
      </button>

      {!registering && bio && !deviceHasPasskey() && (
        <button type="button" className="ghost" onClick={() => faceIdSignIn(onSignedIn)} disabled={busy}><Icon.face /> Sign in with {name}</button>
      )}
      {(status.signupOpen || status.invite === 'valid') && status.hasUsers && (
        <button type="button" className="ghost small" onClick={() => go(registering ? 'login' : 'register')}>
          {registering ? 'I already have an account' : 'Create an account'}
        </button>
      )}
    </Card>
  );
}
